import qs from 'query-string';

import {auth, duplicateOffer, isCancel, getOfferByPIN} from '-/api/api';
import ToastService from '-/services/ToastService';
import {AppSteps} from '-/components/general/AppData';
import BuyerInfoService from '-/services/BuyerInfoService';
import NavigationService from '-/services/NavigationService';
import EmploymentInfoService from '-/services/EmploymentInfoService';
import VehicleInfoService from '-/services/VehicleInfoService';
import SessionService from '-/services/SessionService';
import StipsService from '-/services/StipsService';
import AdditionalIncomeService from '-/services/AdditionalIncomeService';
import UIService from '-/services/UIService';
import ApiService from '-/services/ApiService';
import MixpanelService from '-/services/MixpanelService';
import FunnelService from '-/services/FunnelService';
import {get} from '-/util/lodash';
import {
    VALIDATION_REQUEST_AUTH,
    VALIDATION_REQUEST_AUTH_FAIL,
    VALIDATION_REQUEST_AUTH_SUCCESS,
    PIN_DISCOVERY_FAILURE,
    INCREMENT_FAILED_ATTEMPTS
} from '-/actions/types';
import {getOfferId, isDocumentsMode, isNewOffer, setMyOffers} from '-/util/util';

const validateData = (dispatch, data) => {
    let content = [];

    if (!data) {
        content.push('No data.');
    } else {
        if (!data._id) {
            content.push('No _id.');
        }
        if (data.navigation && data.navigation.currentStepLastSession) {
            if (!AppSteps[data.navigation.currentStepLastSession]) {
                content.push(`${data.navigation.currentStepLastSession} is not a valid step`);
            }
        }
    }

    if (content.length) {
        const error = (
            <div>
                <h1>Invalid Web Service Data</h1>
                <hr />
                {content}
            </div>
        );
        ToastService.addToastWithDuration(ToastService.DANGER, 5000, error);
    }

    return !content.length;
};

const validationFail = (dispatch, authType, opts = {}) => {
    const {failedAttempts, failedAttemptsLimit, offerId} = opts;
    dispatch({
        type: VALIDATION_REQUEST_AUTH_FAIL,
        authType
    });

    const incrementedFailedAttempts = failedAttempts + 1;

    if (incrementedFailedAttempts >= failedAttemptsLimit) {
        window.location.href = `/?offerId=${offerId}`;
    }

    dispatch({
        type: INCREMENT_FAILED_ATTEMPTS,
        payload: {
            failedAttempts: incrementedFailedAttempts
        }
    });

    // reset the auth status to null after a second,
    // which will remove the fail icon
    // todo: in keeping with the rest of the app architecture,
    // this logic should be implemented in a service singleton
    setTimeout(() => {
        dispatch({
            type: VALIDATION_REQUEST_AUTH,
            authType
        });
    }, 1000);
};

export const duplicateOfferSetData = async props => {
    const {otherLoanId} = props;
    const queryString = qs.parse(location.search);
    const {offerId: qsOfferId, baseOffer, ...restOfParams} = queryString;
    const params = qs.stringify(restOfParams);
    let offerId = qsOfferId;

    if (isNewOffer() && baseOffer) {
        offerId = baseOffer;
    }

    const {newOffer = {}} = await duplicateOffer({offerId, otherLoanId});
    const {encodedId} = newOffer;
    let url = `/?offerId=${encodedId}`;

    if (params) {
        url = `${url}&${params}`;
    }

    window.history.pushState({}, document.title, url);

    setData(newOffer, {duplicate: true});
};

const setData = (data, opts = {}) => {
    const {duplicate} = opts;
    const {
        additionalIncome,
        additionalIncome_CoApp,
        buyerInfo,
        buyerInfo_CoApp,
        config,
        employmentInfo,
        employmentInfo_CoApp,
        stepsCompleted,
        stepsCompleted_CoApp
    } = data;

    AdditionalIncomeService.setData({additionalIncome, additionalIncome_CoApp});
    ApiService.initSessionPoll();
    BuyerInfoService.setData({buyerInfo, buyerInfo_CoApp});
    EmploymentInfoService.setData({employmentInfo, employmentInfo_CoApp});
    UIService.stepCompleted('validationInfo');
    UIService.setData({
        fromWorkflow: !!get(data, 'workflow.completedAt'),
        stepsCompleted,
        stepsCompleted_CoApp
    });

    if (duplicate) {
        ApiService.setConfig(config);
        VehicleInfoService.resetData();
        NavigationService.setData({
            lastStep: null,
            hasVinReminder: false,
            hasRegReminder: false,
            setVinReminder: false,
            setRegReminder: false,
            reachedCoApp: false,
            reachedReview: false,
            reachedReview_CoApp: false
        });
    } else {
        VehicleInfoService.setData(data.vehicleInfo);
    }

    // let the success anim run for a second,
    // then move onto next screen
    // todo: in keeping with the rest of the app architecture,
    // this logic should be implemented in a service singleton
    setTimeout(() => {
        if (isStipsMode()) {
            StipsService.enter();
        } else if (isDocumentsMode()) {
            NavigationService.next('documents');
        } else {
            let nextPage = 'buyerInfo';
            const {currentStepLastSession} = NavigationService.getReducerState();

            if (duplicate) {
                nextPage = 'buyerInfo';
            } else if (UIService.getStatus() === 'SUBMITTED') {
                nextPage = 'finalReview';
            } else if (UIService.getFromWorkflow()) {
                nextPage = 'hero';
            } else if (currentStepLastSession) {
                if (isStipsMode() || !isStepStipStep(currentStepLastSession)) {
                    nextPage = currentStepLastSession;
                }
            }

            if (duplicate) {
                UIService.initialize(true);
            }
            
            NavigationService.next(nextPage);
        }
    }, 1000);
};

const initializeValidatedSession = (dispatch, data, authType) => {
    dispatch({
        type: VALIDATION_REQUEST_AUTH_SUCCESS,
        value: data,
        authType
    });

    MixpanelService.validated(true, authType);
    FunnelService.authResult(authType, true);
    NavigationService.setData({authType});

    setData(data);

    if (!SessionService.isCCRMode()) {
        setMyOffers(getOfferId());
    }
};

const processValidation = async(data, dispatch, authType) => {
    const queryString = qs.parse(location.search);
    const copyOffer = 'copyOffer' in queryString;

    if (data.error || !data.isAuthenticated) {
        validationFail(dispatch, authType);
        MixpanelService.validated(false, authType);
        FunnelService.authResult(authType, false, data.error ? 'error' : 'notAuthenticated');
    } else if (copyOffer) {
        duplicateOfferSetData();
    } else if (validateData(dispatch, data)) {
        initializeValidatedSession(dispatch, data, authType);
    } else {
        validationFail(dispatch, authType);
        MixpanelService.validated(false, authType);
        FunnelService.authResult(authType, false, 'invalidData');
    }
};

const authenticate = (authType, value) => {
    return (dispatch, getState) => {
        dispatch({
            type: VALIDATION_REQUEST_AUTH,
            authType
        });

        const state = getState();
        const {failedAttempts, failedAttemptsLimit, offerId} = state.config;

        FunnelService.authAttempted(authType, (failedAttempts || 0) + 1);

        const o = {
            validationType: authType,
            offerId,
            [authType]: value,

            // TODO passport only seems to accept (two) strings, and no objects
            validation: JSON.stringify({
                validationType: authType,
                [authType]: value
            })
        };

        auth(o).then(res => {
            const {data} = res;

            processValidation(data, dispatch, authType, o);
        }).catch(err => {
            if (!isCancel(err)) {
                const failOpts = {
                    failedAttempts,
                    failedAttemptsLimit,
                    offerId
                };
                validationFail(dispatch, authType, failOpts);
                MixpanelService.validated(false, authType);
                FunnelService.authResult(authType, false, 'requestFailed');
            }
        });
    };
};

const discoverPin = pin => {
    return (dispatch, getState) => {
        const state = getState();
        const {creditor} = state;

        getOfferByPIN(pin.toUpperCase(), creditor._id).then(offer => {
            const {encodedId} = offer.data;
            window.location.href = `/?offerId=${encodedId}`;
        }).catch(err => {
            if (!isCancel(err)) {
                dispatch({
                    type: PIN_DISCOVERY_FAILURE,
                    payload: {
                        submittedPin: pin
                    }
                });
            }
        });
    };
};

const isStepStipStep = step => {
    const steps = [
        'stips',
        'stipEdit',
        'stipsStatus',
        'stipsWelcome',
        'stipResubmit',
        'stipsSubmitted'
    ];

    return steps.includes(step);
};

const getStipsMode = () => qs.parse(location.search)['stips'];
const isStipsMode = () => !!getStipsMode();

export {
    authenticate,
    discoverPin
};
